import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import LoanForm from '../components/Forms/LoanForm';
import ContractPreview from '../components/ContractPreview';
import PreQualification from '../components/PreQualification';
import { submitLoanWithContract } from '../utils/LoanAgreementUtils';
import { creditReportError } from '../components/affordability/creditReportView';
import { AuthContext } from '../context/AuthContext';
import useClientHasActiveLoan from '../hooks/useClientHasActiveLoan';
import { useEnvironment } from '../hooks/useEnvironment';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { CheckCircle2, ChevronLeft } from 'lucide-react';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';

// Opens in a panel on the client dashboard ("Apply for a loan").
// resume: open the saved application straight away.
// onSavedForLater: called after "Save and finish later" (the panel closes).
// onReviewSent: called with the API message after a declined application was
// sent to the assessors for review (the panel closes).
const LoanApplication = ({ embedded = false, resume = false, onSavedForLater, onReviewSent }) => {
  const [loanForm, setLoanForm] = useState({});
  const [loanFormErrors, setLoanFormErrors] = useState({});
  const [loanMessage, setLoanMessage] = useState('');
  const [showSubmittedBanner, setShowSubmittedBanner] = useState(false);
  const [interestRates, setInterestRates] = useState([]);
  const [deductionDay, setDeductionDay] = useState('');
  const [bankStatementFile, setBankStatementFile] = useState(null);
  const [payslipFile, setPayslipFile] = useState(null);
  // The client's credit report and its date, when no pre-qualification holds them.
  const [bureauFile, setBureauFile] = useState(null);
  const [bureauDate, setBureauDate] = useState('');
  const [clientPhotoFile, setClientPhotoFile] = useState(null);
  const [canAddLoan, setCanAddLoan] = useState(true);
  const [showContractModal, setShowContractModal] = useState(false);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);
  const [contractUrl, setContractUrl] = useState('');
  const [refreshCounter, setRefreshCounter] = useState(0); // Add counter for re-rendering
  const [loanStatusRefreshKey, setLoanStatusRefreshKey] = useState(0);
  // Cache a pending created loan ID to avoid duplicate creation on retry
  const [pendingOnlineLoanId, setPendingOnlineLoanId] = useState(null);

  // Application phase machine: 'prequalify' | 'form' | 'contract'
  const [phase, setPhase] = useState('prequalify');
  const [assessment, setAssessment] = useState(null);
  const [preqKey, setPreqKey] = useState(0);
  // The repayment period chosen on the pre-qualification result. It sets the
  // loan's duration, and the application cannot go on without one.
  const [termMonths, setTermMonths] = useState(null);
  const assessmentId = assessment?.id || null;
  const chosenTerm = (assessment?.qualification_matrix || [])
    .find(r => Number(r.months) === Number(termMonths)) || null;

  // What the document analysis filled in on the application (the API's
  // `eligibility.prefilled`): the deduction day from the salary date (null:
  // not found, so the client enters it) and the most they may borrow on each
  // term. A client may not change these; they may only ask for less.
  const prefilledFrom = (a) => a?.eligibility?.prefilled || null;
  const prefilledDay = (a) => Number(prefilledFrom(a)?.deduction_day) || null;
  // The most to start the application at: within the recommended instalment
  // (an older result: its maximum).
  const prefilledMax = (a, months) => Number((prefilledFrom(a)?.recommended_principal_by_term || prefilledFrom(a)?.max_principal_by_term)?.[Number(months)]) || null;

  // Reference fields state (persist across modal open/close)
  const [references, setReferences] = useState({
    reference1_name: '',
    reference1_tel: '',
    reference2_name: '',
    reference2_tel: ''
  });

  const { user } = React.useContext(AuthContext);
  const env = useEnvironment();
  const aiEnabled = env?.aiEnabled !== false;
  const { hasActiveLoan, message: activeLoanMessage, loading: loansLoading, error: loansError } = useClientHasActiveLoan(user, loanStatusRefreshKey);

  // State to store manually fetched client profile data
  const [clientProfile, setClientProfile] = useState(null);
  const [clientDataLoading, setClientDataLoading] = useState(false);

  // Fetch client profile data directly instead of relying on AuthContext
  const fetchClientProfile = async () => {
    setClientDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;

  const response = await axios.get(apiUrl('/api/clients/my-profile'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Fetched client profile data:', response.data);
      setClientProfile(response.data);
      setRefreshCounter(prev => prev + 1);
      setClientDataLoading(false);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch client profile:', err);
      setClientDataLoading(false);
      return null;
    }
  };

  // Refresh user data when showing contract preview
  useEffect(() => {
    if (showContractModal) {
      fetchClientProfile();
    }
  }, [showContractModal]);

  React.useEffect(() => {
    const fetchRates = async () => {
      try {
        const token = localStorage.getItem('token');
  const res = await axios.get(apiUrl('/api/interestrates'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const activeRates = res.data.filter(r => r.is_active);
        setInterestRates(activeRates);

        // If staff/admin, don't set default values - let them choose
        // A client's rate is the company rate, set by the API (the loan quote)
        if (!user?.permissions?.includes('ManageLoans')) {
          // Keep a period already chosen on the score: this runs again
          // whenever the signed-in user is refreshed.
          setLoanForm(prevForm => ({
            ...prevForm,
            duration_months: prevForm.duration_months || "1"
          }));
        } else if (activeRates.length > 0) {
          // For staff/admin, initialize with first available rate
          setLoanForm(prevForm => ({
            ...prevForm,
            duration_months: prevForm.duration_months || "1" // Default duration to 1 month, but they can change it
          }));
        }
      } catch {}
    };
    fetchRates();
  }, [user?.permissions]);

  const handlePrequalified = (result, files, choice) => {
    if (!result) return;
    // The same documents feed the final loan submission
    if (files?.payslipFile) setPayslipFile(files.payslipFile);
    if (files?.statementFiles?.length) setBankStatementFile(files.statementFiles[0]);
    const months = Number(choice?.termMonths) || null;
    const maxForTerm = prefilledMax(result, months) || Number(choice?.term?.max_principal) || Number(result.affordability?.recommended_amount) || 0;
    // The amount asked for before the calculation, when it is within what the
    // chosen period allows; otherwise that period's maximum.
    const asked = Number(prefilledFrom(result)?.requested_amount) || 0;
    const startAmount = asked > 0 && (!maxForTerm || asked <= maxForTerm) ? asked : maxForTerm;
    const sameChoice = assessment?.id === result.id && Number(termMonths) === months;
    setAssessment(result);
    setTermMonths(months);
    setLoanForm(prev => {
      // Back from the form with the same period: keep an amount they lowered
      // (the loan quote says if it is more than they qualify for).
      const current = parseFloat(prev.principal_amount);
      const keep = sameChoice && Number.isFinite(current) && current > 0 && (!maxForTerm || current <= maxForTerm);
      return {
        ...prev,
        duration_months: months ? String(months) : prev.duration_months,
        principal_amount: keep ? prev.principal_amount : String(startAmount || ''),
      };
    });
    // The day the documents set; when none was found, keep a day already typed.
    const day = prefilledDay(result);
    setDeductionDay(prev => (day ? String(day) : (sameChoice ? prev : String(result.recommended_deduction_day || ''))));
    setLoanFormErrors({});
    setPhase('form');
  };

  const resetPrequalification = () => {
    setPhase('prequalify');
    setAssessment(null);
    setTermMonths(null);
    // Submitting deletes the saved application on the server too.
    setSavedDraft(null);
    setPreqKey(k => k + 1);
  };

  // Back to the result, with its documents and the chosen period kept, to
  // look at it again or pick another period.
  const backToScore = () => {
    setLoanFormErrors({});
    setPhase('prequalify');
  };

  // ── Saved application (finish later) ─────────────────────────────────────
  // One per client, on the server (/api/loan-drafts/mine), so it can be
  // finished on another device or in the app. It keeps the answers and the
  // chosen score; the photo is taken again, because it has to be live.
  const isClient = !user?.permissions?.includes('ManageLoans');
  // A client applying on an assessment cannot change what it filled in.
  const lockPrefilled = isClient && aiEnabled && !!assessment;
  const lockedDeductionDay = lockPrefilled ? prefilledDay(assessment) : null;
  const maxPrincipal = lockPrefilled && termMonths ? (prefilledMax(assessment, termMonths) || Number(chosenTerm?.max_principal) || null) : null;
  // The deduction day sent with the application.
  const appliedDeductionDay = lockedDeductionDay ? String(lockedDeductionDay) : deductionDay;
  const [savedDraft, setSavedDraft] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);

  useEffect(() => {
    if (!isClient || !aiEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-drafts/mine'), { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled && res.data?.credit_assessment_id) setSavedDraft(res.data);
      } catch {
        // No saved application (404) — nothing to offer.
      }
    })();
    return () => { cancelled = true; };
  }, [isClient, aiEnabled]);

  const saveForLater = async () => {
    if (!assessment?.id) return;
    setDraftBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(apiUrl('/api/loan-drafts/mine'), {
        credit_assessment_id: assessment.id,
        term_months: termMonths,
        principal_amount: loanForm.principal_amount,
        loan_reason_id: loanForm.loan_reason_id,
        deduction_day: appliedDeductionDay,
        references,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSavedDraft(res.data);
      if (typeof onSavedForLater === 'function') {
        onSavedForLater();
        return;
      }
      setLoanMessage('Your application is saved. Continue it from your dashboard any time to finish it — you will only need to take your photo again.');
    } catch (err) {
      setLoanMessage(err.response?.data?.error || 'Your application could not be saved. Please try again.');
    } finally {
      setDraftBusy(false);
    }
  };

  // From the dashboard's "Continue my application" (?resume=1): open the
  // saved application straight away, once it has loaded.
  const location = useLocation();
  const autoResumed = useRef(false);
  useEffect(() => {
    if (autoResumed.current || !savedDraft || assessment) return;
    if (!resume && !new URLSearchParams(location.search).has('resume')) return;
    autoResumed.current = true;
    resumeDraft();
  }, [savedDraft, assessment, location.search, resume]);

  const discardDraft = async () => {
    setDraftBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(apiUrl('/api/loan-drafts/mine'), { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* already gone */ }
    setSavedDraft(null);
    setDraftBusy(false);
  };

  const resumeDraft = async () => {
    if (!savedDraft?.credit_assessment_id) return;
    setDraftBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl(`/api/prequalify/${savedDraft.credit_assessment_id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const saved = res.data;
      // Whether the score can still be applied on, and on which terms, is the
      // API's call (the assessment's `eligibility`).
      const verdict = saved?.eligibility;
      const termOk = (verdict?.available_terms || []).map(Number).includes(Number(savedDraft.term_months));
      const row = (saved?.qualification_matrix || []).find(r => Number(r.months) === Number(savedDraft.term_months));
      if (!saved || !verdict?.can_proceed || !termOk || !row) {
        setLoanMessage(verdict?.message || 'The score your saved application used is no longer available. Please check your documents again to continue.');
        await discardDraft();
        return;
      }
      // The amount saved, never more than the term's maximum (the loan quote
      // has the last word).
      const max = prefilledMax(saved, row.months) || Number(row.max_principal) || 0;
      const savedAmount = Number(savedDraft.principal_amount);
      const amount = Number.isFinite(savedAmount) && savedAmount > 0 && (!max || savedAmount <= max) ? savedAmount : max;
      setAssessment(saved);
      setTermMonths(Number(row.months));
      setLoanForm(prev => ({
        ...prev,
        duration_months: String(row.months),
        principal_amount: String(amount || row.max_principal),
        loan_reason_id: savedDraft.loan_reason_id != null ? String(savedDraft.loan_reason_id) : prev.loan_reason_id,
      }));
      // The documents' deduction day wins over the one saved.
      setDeductionDay(String(prefilledDay(saved) || savedDraft.deduction_day || saved.recommended_deduction_day || ''));
      if (savedDraft.references) {
        setReferences(prev => ({ ...prev, ...Object.fromEntries(Object.entries(savedDraft.references).map(([k, v]) => [k, v || ''])) }));
      }
      setLoanFormErrors({});
      setPhase('form');
    } catch (err) {
      setLoanMessage(err.response?.data?.error || 'Your saved application could not be opened. Please try again.');
    } finally {
      setDraftBusy(false);
    }
  };

  const validateLoanForm = () => {
    let valid = true;
    const errors = {};
    if (!loanForm.principal_amount || isNaN(loanForm.principal_amount)) {
      errors.principal_amount = 'Principal amount is required and must be a number';
      valid = false;
    }
    if (aiEnabled && assessment && !chosenTerm) {
      errors.duration_months = 'Go back to your score and choose a repayment period';
      valid = false;
    }
    if (!loanForm.duration_months || isNaN(loanForm.duration_months)) {
      errors.duration_months = 'Duration is required and must be a number';
      valid = false;
    }
    // Staff choose the rate; a client's is set by the API.
    if (!isClient && (!loanForm.interest_rate || isNaN(loanForm.interest_rate))) {
      errors.interest_rate = 'Interest rate is required';
      valid = false;
    }
    // The amount, term and deduction day are checked by the API (the loan
    // quote, against what the client qualified for); its messages are shown
    // as they are.
    if (loanForm.quote_errors) {
      Object.entries(loanForm.quote_errors).forEach(([field, msg]) => {
        if (!errors[field]) errors[field] = msg;
      });
      valid = false;
    } else if (!loanForm.monthly_payment && !errors.principal_amount) {
      errors.principal_amount = 'The loan amounts are still being worked out. Please try again in a moment.';
      valid = false;
    }
    if (!loanForm.start_date) {
      errors.start_date = 'Start date is required';
      valid = false;
    }
    if (!loanForm.due_date) {
      errors.due_date = 'Due date is required';
      valid = false;
    }
    // Documents are collected during pre-qualification; a completed assessment
    // satisfies the requirement. The file type and size are checked by the API,
    // whose message is shown if a file is refused.
    if (!bankStatementFile && (!aiEnabled || !assessment)) {
      errors.bankStatementFile = 'Bank statement is required';
      valid = false;
    }
    if (!payslipFile && (!aiEnabled || !assessment)) {
      errors.payslipFile = 'Payslip is required';
      valid = false;
    }
    // The credit report is mandatory on every application.
    const bureauError = !aiEnabled || !assessment ? creditReportError({ file: bureauFile, reportDate: bureauDate, required: true }) : null;
    if (bureauError) {
      errors.bureauReport = bureauError;
      valid = false;
    }
    if (!clientPhotoFile) {
      errors.clientPhotoFile = 'Client photo is required';
      valid = false;
    }
    setLoanFormErrors(errors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateLoanForm()) return;

    // Set loading state
    setClientDataLoading(true);
    setLoanMessage('Preparing contract preview...');

    try {
      // Fetch fresh client data before showing contract
      const freshClientData = await fetchClientProfile();

      if (!freshClientData) {
        setLoanMessage('Failed to load client data. Please try again.');
        return;
      }

      console.log("Fresh client data for contract:", freshClientData);

      // Increment counter to force re-render with fresh data
      setRefreshCounter(prev => prev + 1);
      setLoanMessage('');
      setShowContractModal(true);
      setPhase('contract');
    } catch (err) {
      console.error('Error preparing contract preview:', err);
      setLoanMessage('Error preparing contract preview. Please try again.');
    } finally {
      setClientDataLoading(false);
    }
  };

  return (
    <div className={embedded ? 'w-full' : 'w-full min-h-screen p-2 sm:p-4 bg-gray-50 dark:bg-slate-900'}>
      {loanMessage && (
        <div className="mb-2 p-3 rounded border flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700/50">
          <span>{loanMessage}</span>
          <button
            onClick={() => setLoanMessage('')}
            className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-700 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            &times;
          </button>
        </div>
      )}
      {showSubmittedBanner && (
        <div className="mb-4 sm:mb-6 rounded-2xl border border-emerald-200 dark:border-emerald-700/40 bg-gradient-to-r from-emerald-50 via-white to-blue-50 dark:from-emerald-900/20 dark:via-slate-800 dark:to-blue-900/20 p-4 sm:p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-slate-100">Loan Submitted</h2>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 mt-1">Contract queued for staff signature.</p>
              {loansLoading ? (
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">Checking if you can apply for another loan...</p>
              ) : hasActiveLoan ? (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-2">Your current loan is being processed.</p>
              ) : (
                <button
                  onClick={() => {
                    setShowSubmittedBanner(false);
                    setLoanMessage('');
                  }}
                  className="mt-3 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
                >
                  Apply for Another Loan
                </button>
              )}
            </div>
            <button
              onClick={() => setShowSubmittedBanner(false)}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl leading-none"
              aria-label="Dismiss"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {loansLoading ? (
        <div className="p-4 text-blue-700 dark:text-blue-300">Checking loan status...</div>
      ) : loansError ? (
        <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg border border-red-300 dark:border-red-700/50 font-semibold">
          {loansError}
        </div>
      ) : hasActiveLoan ? (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/20 p-6 sm:p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <svg className="h-7 w-7 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-amber-900 dark:text-amber-200 mb-2">Active Loan in Progress</h2>
          <p className="text-amber-800 dark:text-amber-300 text-sm sm:text-base max-w-md mx-auto">
            {activeLoanMessage || 'You already have an active loan. You cannot submit a new loan application until your current loan has been fully settled, cancelled, or rejected.'}
          </p>
          <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
            Please visit your <strong>Dashboard</strong> to view or manage your current loan.
          </p>
        </div>
      ) : (aiEnabled && phase === 'prequalify') ? (
        <>
          {savedDraft && !assessment && (
            <div className="mb-4 rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">You have a saved application</p>
                <p className="text-xs text-blue-800 dark:text-blue-300 mt-0.5">
                  {savedDraft.principal_amount ? <>{toCurrency(savedDraft.principal_amount)} over </> : null}
                  {savedDraft.term_months ? <>{savedDraft.term_months} month{Number(savedDraft.term_months) === 1 ? '' : 's'}</> : null}
                  {savedDraft.saved_at ? <> · saved {formatDateDMY(savedDraft.saved_at)}</> : null}
                  {' '}— pick up where you left off.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={discardDraft}
                  disabled={draftBusy}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={resumeDraft}
                  disabled={draftBusy}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {draftBusy ? 'Opening…' : 'Continue my application'}
                </button>
              </div>
            </div>
          )}
          <PreQualification
            key={preqKey}
            onCompleted={handlePrequalified}
            initialAssessment={assessment}
            initialTermMonths={termMonths}
            onReviewSent={(message) => {
              resetPrequalification();
              setLoanStatusRefreshKey((k) => k + 1);
              if (typeof onReviewSent === 'function') {
                onReviewSent(message);
                return;
              }
              setLoanMessage(message);
            }}
          />
        </>
      ) : (phase === 'form' || phase === 'prequalify') ? (
        <form onSubmit={handleSubmit}>
          {aiEnabled && (
            <div className="mb-4 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  {chosenTerm ? (
                    <>Pre-qualified for up to <strong>{toCurrency(chosenTerm.max_principal)}</strong> over <strong>{chosenTerm.months} month{Number(chosenTerm.months) === 1 ? '' : 's'}</strong> (you may apply for less)</>
                  ) : (
                    <>Pre-qualified — go back to your score to choose a repayment period</>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={backToScore}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-100 dark:bg-slate-800 dark:text-emerald-300 dark:border-emerald-700 dark:hover:bg-slate-700 transition-colors"
              >
                <ChevronLeft size={13} />
                Back to my score
              </button>
            </div>
          )}
          <LoanForm
            loanForm={loanForm}
            setLoanForm={setLoanForm}
            loanFormErrors={loanFormErrors}
            interestRates={interestRates}
            deductionDay={appliedDeductionDay}
            setDeductionDay={setDeductionDay}
            bankStatementFile={bankStatementFile}
            setBankStatementFile={setBankStatementFile}
            payslipFile={payslipFile}
            setPayslipFile={setPayslipFile}
            bureauFile={bureauFile}
            setBureauFile={setBureauFile}
            bureauDate={bureauDate}
            setBureauDate={setBureauDate}
            clientPhotoFile={clientPhotoFile}
            setClientPhotoFile={setClientPhotoFile}
            canAddLoan={canAddLoan}
            showApplicationType={user?.permissions?.includes('ManageLoans')} // Enable editable fields for staff/admin
            hideDocuments={aiEnabled} // Bank statement & payslip already collected during pre-qualification
            durationChosenFromScore={aiEnabled && !!chosenTerm}
            creditAssessmentId={(aiEnabled && assessmentId) || null}
            lockedDeductionDay={lockedDeductionDay}
            lockDates={isClient}
            maxPrincipal={maxPrincipal}
          />
          <div className="flex flex-wrap justify-end gap-4 pt-6 border-t dark:border-slate-700 mt-6">
            {isClient && aiEnabled && assessment?.id && (
              <button
                type="button"
                onClick={saveForLater}
                disabled={draftBusy}
                title="Keep your answers and finish the application later"
                className="px-6 py-3 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 font-semibold hover:bg-blue-50 dark:hover:bg-slate-800 transition duration-200 disabled:opacity-50"
              >
                {draftBusy ? 'Saving…' : 'Save and finish later'}
              </button>
            )}
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canAddLoan}
            >
              Submit Loan Application
            </button>
          </div>
        </form>
      ) : clientDataLoading ? (
        <div className="p-6 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
          <p className="text-gray-700 dark:text-slate-300">Loading client information...</p>
        </div>
      ) : (
        <ContractPreview
          key={refreshCounter} // Add key to force re-rendering
          loanData={loanForm}
          clientData={{
            // Use clientProfile data if available, fall back to user context
            // Personal info
            id: clientProfile?.personal_info?.id || user?.personal_info?.id,
            first_name: clientProfile?.personal_info?.first_name || user?.personal_info?.first_name,
            middle_name: clientProfile?.personal_info?.middle_name || user?.personal_info?.middle_name,
            last_name: clientProfile?.personal_info?.last_name || user?.personal_info?.last_name,
            id_passport_no: clientProfile?.personal_info?.id_passport_no || user?.personal_info?.id_passport_no,
            email: clientProfile?.personal_info?.email || user?.personal_info?.email,
            phone_number: clientProfile?.personal_info?.phone_number || user?.personal_info?.phone_number,
            postal_address: clientProfile?.personal_info?.postal_address || user?.personal_info?.postal_address,
            residential_address: clientProfile?.personal_info?.residential_address || user?.personal_info?.residential_address,
            marital_status: clientProfile?.personal_info?.marital_status || user?.personal_info?.marital_status,
            gender: clientProfile?.personal_info?.gender || user?.personal_info?.gender,
            // Occupation info
            occupation: clientProfile?.occupation_info?.occupation || user?.occupation_info?.occupation,
            employer_name: clientProfile?.occupation_info?.employer_name || user?.occupation_info?.employer_name,
            employer_phone: clientProfile?.occupation_info?.employer_phone || user?.occupation_info?.employer_phone,
            employer_address: clientProfile?.occupation_info?.employer_address || user?.occupation_info?.employer_address,
            payslip_number: clientProfile?.occupation_info?.payslip_number || user?.occupation_info?.payslip_number,
            // Bank info
            bank_name: clientProfile?.bank_info?.bank_name || user?.bank_info?.bank_name,
            account_number: clientProfile?.bank_info?.account_number || user?.bank_info?.account_number,
            branch_code: clientProfile?.bank_info?.branch_code || user?.bank_info?.branch_code,
            account_type: clientProfile?.bank_info?.account_type || user?.bank_info?.account_type
          }}
          showPreviewHeader={false}
          references={references}
          setReferences={setReferences}
          onEdit={() => {
            setShowContractModal(false);
            setPhase('form');
          }}
          onGenerate={async (clientDataWithSignature) => {
            // New flow: create loan first, then submit client signatures for staff review
            setIsGeneratingContract(true);
            try {
              const clientIdToUse = clientProfile?.personal_info?.id || user?.personal_info?.id;
              // Reuse existing pending loan ID if present to avoid duplicate creation
              let loanId = pendingOnlineLoanId;
              if (!loanId) {
                const loanResult = await submitLoanWithContract(
                  { ...loanForm, application_type: 'Online', deduction_day: appliedDeductionDay },
                  clientIdToUse,
                  undefined,
                  bankStatementFile,
                  payslipFile,
                  clientPhotoFile,
                  (aiEnabled && assessmentId) || undefined,
                  { bureauReport: bureauFile ? { file: bureauFile, date: bureauDate } : null }
                );
                if (!loanResult.success || !loanResult.data?.loan?.id) {
                  setLoanMessage(loanResult.message || 'Failed to submit loan');
                  setIsGeneratingContract(false);
                  return;
                }
                loanId = loanResult.data.loan.id;
                setPendingOnlineLoanId(loanId);
              }

              const token = localStorage.getItem('token');
              await axios.post(
                apiUrl(`/api/loans/${loanId}/client-sign`),
                {
                  references: {
                    reference1_name: clientDataWithSignature.reference1_name,
                    reference1_tel: clientDataWithSignature.reference1_tel,
                    reference2_name: clientDataWithSignature.reference2_name,
                    reference2_tel: clientDataWithSignature.reference2_tel,
                  },
                  signatureLocations: clientDataWithSignature.signatureLocations,
                  // How the money is paid out once approved (mandatory).
                  payout: clientDataWithSignature.payout,
                  signature: clientDataWithSignature.signature,
                  initials: clientDataWithSignature.initials,
                  witness1Signature: clientDataWithSignature.witness1Signature,
                  witness1Initials: clientDataWithSignature.initials?.witness1Initials,
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );

              setShowContractModal(false);
              setLoanForm({});
              setLoanFormErrors({});
              setBankStatementFile(null);
              setPayslipFile(null); setBureauFile(null); setBureauDate('');
              setClientPhotoFile(null);
              setDeductionDay('');
              setContractUrl('');
              setReferences({ reference1_name: '', reference1_tel: '', reference2_name: '', reference2_tel: '' });
              setPendingOnlineLoanId(null);
              resetPrequalification();
              setLoanStatusRefreshKey((k) => k + 1);
              setShowSubmittedBanner(true);
            } catch (err) {
              setLoanMessage(err.response?.data?.error || err.response?.data?.message || 'Failed to submit signatures');
            }
            setIsGeneratingContract(false);
          }}
          loading={isGeneratingContract}
        />
      )}
    </div>
  );
};

export default LoanApplication;
